// components/FollowFormModal/index.tsx
// フォロー登録・更新フォームモーダルの汎用コンポーネント
//
// 使用箇所:
//   - src/components/FollowListModal/index.tsx（フォロー詳細モーダル内の編集ボタン）
//   - src/pages/BusinessCardDetailPage.tsx（フォロー追加・更新モーダル）

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Form, Input, Select, Checkbox, Space,
  DatePicker, Spin, Dropdown,
} from 'antd';
import {
  RobotOutlined, SoundOutlined, CloseCircleFilled,
  PaperClipOutlined, EyeOutlined, DownloadOutlined, DeleteOutlined, UploadOutlined,
  SendOutlined, HistoryOutlined, DownOutlined, EditOutlined,
  ShareAltOutlined, LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { isFutureDate, isPastDate } from '../../utils/dateUtils';
import './FollowFormModal.css';
import { optionFieldAPI, OptionFieldListResponse } from '../../services/option-field-api';
import OptionFieldGroup from '../OptionFields/OptionFieldGroup';
import { OptionFieldValidator } from '../../utils/optionFieldValidation';
import { MODAL_Z } from '../../utils/modalZIndex';
import { AppointmentTimePanel, AppointmentInternalAttendeesSection } from '../AppointmentTimePanel';
import CommentSection from '../CommentSection';
import { FollowComment } from '../../types/follow';

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface FollowFormContactUser {
  userno: number;
  username: string;
  deptname: string;
}

export interface FollowFormFileItem {
  uid: string;
  name: string;
  size: number;
  attachfileno?: number;
}

export interface OnsiteCustomer {
  customerno: number;
  customername: string;
  companyname: string;
  deptname: string;
}

export interface FollowFormValues {
  date: string;
  assigneeUserno: number | null;
  content: string;
  isProvisional: boolean;
  isTodo: boolean;
  fileList: FollowFormFileItem[];
  // 訪問/来社
  isOnsite: boolean;
  followScheduledTime: string;
  followAppointment: string;
  followVisitPurpose: string;
  followStartTime: string;
  followEndTime: string;
  onsiteCustomers: OnsiteCustomer[];
  selectedInternalAttendees: number[];
  // 拡張フィールド
  optionFieldValues: Record<string, any>;
}

export interface FollowFormModalProps {
  /** モーダルの表示状態 */
  open: boolean;
  /** 新規登録か編集かを示すモード */
  mode: 'new' | 'edit';
  /** キャンセル時のコールバック */
  onCancel: () => void;
  /** 送信確定時のコールバック（バリデーション済み値を渡す） */
  onSubmit: (values: FollowFormValues) => void;
  /** 担当者候補一覧 */
  contactUsers: FollowFormContactUser[];
  /** 担当者リストのローディング状態 */
  isLoadingContactUsers?: boolean;
  /** モーダルを開いたときの初期値（編集時に渡す） */
  initialValues?: Partial<FollowFormValues>;
  /** 新規登録時にデフォルト担当者として自動選択する userno */
  defaultAssigneeUserno?: number | null;
  /** モーダルタイトル（デフォルト: "フォロー更新"） */
  title?: string;
  /** 送信ボタンのラベル（デフォルト: "更新"） */
  submitLabel?: string;
  /** モーダルの zIndex */
  zIndex?: number;
  // ---- 外部ハンドラ（BusinessCardDetailPage から渡す）----
  /** アシスト入力ボタン押下時 */
  onAssistClick?: () => void;
  /** 音声入力ボタン押下時 */
  onVoiceClick?: () => void;
  // ---- 同席者（客先）controlled mode ----
  /** 外部管理の同席者一覧（提供時はこちらを使用） */
  controlledOnsiteCustomers?: OnsiteCustomer[];
  /** 同席者（客先）を個別削除したとき（controlled mode 用） */
  onOnsiteCustomerRemove?: (customerno: number) => void;
  /** 同席者（客先）追加ボタン押下時 */
  onCustomerSelectOpen?: () => void;
  /** 同席者（客先）全削除ボタン押下時 */
  onOnsiteCustomersClear?: () => void;
  // ---- 添付ファイル controlled mode（BusinessCardDetailPage から渡す場合）----
  /** 外部管理のファイル一覧（提供時はこちらを表示） */
  controlledFileList?: FollowFormFileItem[];
  /** ファイル追加ボタン押下時（担当者バリデーション後に現在値を渡す） */
  onFileAdd?: (values: FollowFormValues) => void;
  /** ファイルプレビューボタン押下時 */
  onFilePreview?: (file: FollowFormFileItem) => void;
  /** ファイルダウンロードボタン押下時 */
  onFileDownload?: (file: FollowFormFileItem) => void;
  /** ファイル削除ボタン押下時（index は表示リスト上のインデックス） */
  onFileDelete?: (file: FollowFormFileItem, index: number) => void;
  /**
   * モーダルが開いたとき、内容テキストの setter を渡すコールバック。
   * 親コンポーネント（BCD）がアシスト入力・音声入力でテキストを挿入するために使用する。
   */
  onContentSetterReady?: (setter: React.Dispatch<React.SetStateAction<string>>) => void;
  /** 通知送信ボタン押下時（渡した場合のみ通知セクションを表示） */
  onNotifSendClick?: (assigneeUserno: number | null, isTodo: boolean) => void;
  /** 通知履歴ボタン押下時 */
  onNotifHistoryClick?: () => void;
  /** Flutterフッターの高さ（px）。0の場合は非Flutter環境として扱う */
  flutterFooterHeight?: number;
  /** 関連フォロー表示ボタン押下時のコールバック（省略時はボタン非表示） */
  onShowRelatedFollow?: () => void;
  /** フォローの紐づけボタン押下時のコールバック（省略時はボタン非表示） */
  onShowLinkFollow?: () => void;
  /** コメント一覧（省略時はコメントセクション自体を非表示） */
  comments?: FollowComment[];
  /** コメント追加ボタン押下時 */
  onAddComment?: () => void;
  /** コメント編集ボタン押下時 */
  onEditComment?: (comment: FollowComment) => void;
  /** コメント削除ボタン押下時 */
  onDeleteComment?: (comment: FollowComment) => void;
  /** ログインユーザーのuserno（コメントの本人編集/削除判定に使用。クライアント側チェックのみ） */
  currentUserNo?: number | null;
}

// ----------------------------------------------------------------
// ヘルパー
// ----------------------------------------------------------------

// defaultAssigneeUserno が担当者候補に含まれればそれを、含まれなければ先頭を返す
const resolveDefaultAssignee = (
  defaultUserno: number | null | undefined,
  users: FollowFormContactUser[],
): number | null => {
  if (defaultUserno == null || users.length === 0) return null;
  return users.some(u => u.userno === defaultUserno) ? defaultUserno : users[0].userno;
};

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const FollowFormModal: React.FC<FollowFormModalProps> = ({
  open,
  mode,
  onCancel,
  onSubmit,
  contactUsers,
  isLoadingContactUsers,
  initialValues,
  defaultAssigneeUserno,
  title = 'フォロー更新',
  submitLabel = '更新',
  zIndex,
  onAssistClick,
  onVoiceClick,
  controlledOnsiteCustomers,
  onOnsiteCustomerRemove,
  onCustomerSelectOpen,
  onOnsiteCustomersClear,
  controlledFileList,
  onFileAdd,
  onFilePreview,
  onFileDownload,
  onFileDelete,
  onContentSetterReady,
  onNotifSendClick,
  onNotifHistoryClick,
  flutterFooterHeight = 0,
  onShowRelatedFollow,
  onShowLinkFollow,
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  currentUserNo,
}) => {
  // ---- 基本フィールド ----
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [assigneeUserno, setAssigneeUserno] = useState<number | null>(null);
  const [content, setContent] = useState<string>('');
  const [isProvisional, setIsProvisional] = useState<boolean>(false);
  const [isTodo, setIsTodo] = useState<boolean>(false);
  const [internalFileList, setInternalFileList] = useState<FollowFormFileItem[]>([]);
  // 予定/ToDo処理ボタンを押下済みか（押下後に非活性化する）
  const [todoProcessed, setTodoProcessed] = useState<boolean>(false);
  // 予定/ToDo処理の確認ダイアログ表示状態
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);

  // ---- 訪問/来社 ----
  const [isOnsite, setIsOnsite] = useState<boolean>(false);
  const [followScheduledTime, setFollowScheduledTime] = useState<string>('');
  const [followAppointment, setFollowAppointment] = useState<string>('');
  const [followVisitPurpose, setFollowVisitPurpose] = useState<string>('');
  const [followStartTime, setFollowStartTime] = useState<string>('');
  const [followEndTime, setFollowEndTime] = useState<string>('');
  const [internalOnsiteCustomers, setInternalOnsiteCustomers] = useState<OnsiteCustomer[]>([]);
  const [selectedInternalAttendees, setSelectedInternalAttendees] = useState<number[]>([]);

  // ---- 拡張フィールド ----
  const [followOptionFields, setFollowOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [loadingFollowOptionFields, setLoadingFollowOptionFields] = useState<boolean>(false);
  const [followOptionFieldValues, setFollowOptionFieldValues] = useState<Record<string, any>>({});
  const [followOptionFieldErrors, setFollowOptionFieldErrors] = useState<Record<string, string>>({});

  // ---- UI 状態 ----
  const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);

  /*
   * モーダルを閉じる時に DatePicker のドロップダウンを必ず閉じる。
   * DatePicker のポップアップは body 配下にポータルで描画されるうえ、
   * global.css の `.ant-picker-dropdown { position:fixed; top:50%; ... !important }` で
   * 強制センタリングしているため、antd の close アニメ／状態管理だけでは
   * 画面から消えないケースがある。
   *
   * 対策として、antd 自身が close 時に付与する `ant-picker-dropdown-hidden` クラスを
   * 手動で付与して `display:none` を効かせる。要素の削除ではなくクラス付与なので、
   * 次回 antd が open=true で再描画する際に React が className を上書きして自然に再表示される。
   */
  const closeDatePickerDropdownDom = () => {
    document
      .querySelectorAll('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)')
      .forEach((el) => el.classList.add('ant-picker-dropdown-hidden'));
  };

  const handleCancel = () => {
    setDatePickerOpen(false);
    closeDatePickerDropdownDom();
    onCancel();
  };

  /*
   * onCancel を経由しない閉じ方（親が直接 open を切り替える等）でも反応するフェイルセーフ。
   */
  useEffect(() => {
    if (!open) {
      setDatePickerOpen(false);
      closeDatePickerDropdownDom();
    }
  }, [open]);

  // ---- フォロー日付チェック ----
  const isFuture = isFutureDate(date);
  const isPast = isPastDate(date);

  // フォーム先頭スクロール用 ref
  const formTopRef = useRef<HTMLDivElement>(null);

  // 表示用（controlled 優先）
  const displayOnsiteCustomers = controlledOnsiteCustomers ?? internalOnsiteCustomers;
  const displayFileList = controlledFileList ?? internalFileList;

  // ----------------------------------------------------------------
  // 初期値適用（open トリガー）
  // ----------------------------------------------------------------

  useEffect(() => {
    if (!open) return;

    const initDate = initialValues?.date ?? new Date().toISOString().split('T')[0];
    const initContent = initialValues?.content ?? '';
    const initIsProvisional = initialValues?.isProvisional ?? false;
    const initIsTodo = initialValues?.isTodo ?? false;
    const initFileList = initialValues?.fileList ?? [];
    const initIsOnsite = initialValues?.isOnsite ?? false;
    const initScheduledTime = initialValues?.followScheduledTime ?? '';
    const initAppointment = initialValues?.followAppointment ?? '';
    const initVisitPurpose = initialValues?.followVisitPurpose ?? '';
    const initStartTime = initialValues?.followStartTime ?? '';
    const initEndTime = initialValues?.followEndTime ?? '';
    const initOnsiteCustomers = initialValues?.onsiteCustomers ?? [];
    const initInternalAttendees = initialValues?.selectedInternalAttendees ?? [];
    const initOptionFieldValues = initialValues?.optionFieldValues ?? {};

    setDate(initDate);
    setContent(initContent);
    setIsProvisional(initIsProvisional);
    setIsTodo(initIsTodo);
    setInternalFileList(initFileList);
    setIsOnsite(initIsOnsite);
    setFollowScheduledTime(initScheduledTime);
    setFollowAppointment(initAppointment);
    setFollowVisitPurpose(initVisitPurpose);
    setFollowStartTime(initStartTime);
    setFollowEndTime(initEndTime);
    // controlled mode 時は親が管理するため内部状態を更新しない
    if (!controlledOnsiteCustomers) {
      setInternalOnsiteCustomers(initOnsiteCustomers);
    }
    setSelectedInternalAttendees(initInternalAttendees);
    setFollowOptionFieldValues(initOptionFieldValues);
    setFollowOptionFieldErrors({});
    setShowValidationErrors(false);
    setDatePickerOpen(false);
    setTodoProcessed(false);
    setClearConfirmOpen(false);

    // 親コンポーネントへ content setter を渡す（アシスト/音声入力でのテキスト挿入用）
    onContentSetterReady?.(setContent);

    // 担当者の自動選択
    if (initialValues?.assigneeUserno != null) {
      setAssigneeUserno(initialValues.assigneeUserno);
    } else {
      setAssigneeUserno(resolveDefaultAssignee(defaultAssigneeUserno, contactUsers));
    }


    // 拡張フィールド定義を取得
    fetchOptionFields();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // 日付が未来日付に変わったら予定/ToDo を自動ON
  useEffect(() => {
    if (isFuture) {
      setIsTodo(true);
    }
  }, [isFuture]);

  // ----------------------------------------------------------------
  // API 取得
  // ----------------------------------------------------------------

  const fetchOptionFields = async () => {
    try {
      setLoadingFollowOptionFields(true);
      const result = await optionFieldAPI.getOptionFieldList();
      if (result.statuscode === 200) {
        setFollowOptionFields(result);
      }
    } catch (e) {
      console.error('拡張フィールド取得エラー:', e);
    } finally {
      setLoadingFollowOptionFields(false);
    }
  };

  // ----------------------------------------------------------------
  // 拡張フィールド変更ハンドラ
  // ----------------------------------------------------------------

  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    setFollowOptionFieldValues(prev => ({ ...prev, [fieldKey]: value }));
    if (followOptionFieldErrors[fieldKey]) {
      setFollowOptionFieldErrors(prev => {
        const { [fieldKey]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // ----------------------------------------------------------------
  // 送信処理（バリデーション込み）
  // ----------------------------------------------------------------

  const handleOk = () => {
    setShowValidationErrors(true);
    if (!assigneeUserno) return;

    // 拡張フィールドのバリデーション
    if (followOptionFields?.followoptionfieldlist) {
      const result = OptionFieldValidator.validateFields(
        followOptionFieldValues,
        followOptionFields.followoptionfieldlist
      );
      if (!result.isValid) {
        const errors: Record<string, string> = {};
        result.errors.forEach(err => { errors[err.field] = err.message; });
        setFollowOptionFieldErrors(errors);
        return;
      }
      setFollowOptionFieldErrors({});
    }

    onSubmit({
      date,
      assigneeUserno,
      content,
      isProvisional,
      isTodo,
      fileList: displayFileList,
      isOnsite,
      followScheduledTime,
      followAppointment,
      followVisitPurpose,
      followStartTime,
      followEndTime,
      onsiteCustomers: displayOnsiteCustomers,
      selectedInternalAttendees,
      optionFieldValues: followOptionFieldValues,
    });
  };

  // ----------------------------------------------------------------
  // クリア処理
  // ----------------------------------------------------------------

  const handleClear = () => {
    setIsOnsite(false);
    // controlled mode 時は親の state を消去、非 controlled 時は内部 state を消去
    if (controlledOnsiteCustomers) {
      onOnsiteCustomersClear?.();
    } else {
      setInternalOnsiteCustomers([]);
    }
    setFollowScheduledTime('');
    setFollowStartTime('');
    setFollowEndTime('');
    setFollowAppointment('');
    setFollowVisitPurpose('');
    setSelectedInternalAttendees([]);
    setDate(new Date().toISOString().split('T')[0]);
    setContent('');
    setIsProvisional(false);
    setIsTodo(false);
    setFollowOptionFieldValues({});
    setFollowOptionFieldErrors({});
    setShowValidationErrors(false);

    // 担当者をデフォルトに戻す
    const resolvedAssignee = resolveDefaultAssignee(defaultAssigneeUserno, contactUsers);
    if (resolvedAssignee != null) {
      setAssigneeUserno(resolvedAssignee);
    }

    // フォーム先頭にスクロール
    const modalBody = formTopRef.current?.closest('.ant-modal-body');
    modalBody?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ----------------------------------------------------------------
  // 予定/ToDo処理
  // ----------------------------------------------------------------

  const applyTodoProcess = (clearContent: boolean) => {
    // フォロー日付を今日にする
    setDate(new Date().toISOString().split('T')[0]);
    // 予定/ToDo チェックを外す
    setIsTodo(false);
    if (clearContent) {
      // フォロー内容をクリア
      setContent('');
      // 担当者をログインユーザーにする
      if (defaultAssigneeUserno != null) {
        setAssigneeUserno(resolveDefaultAssignee(defaultAssigneeUserno, contactUsers));
      }
    }
    // ボタンを非活性化
    setTodoProcessed(true);
  };

  // ボタン押下 → 確認ダイアログを開く
  const handleProcessTodo = () => {
    setClearConfirmOpen(true);
  };

  // 確認ダイアログ「はい」: フォロー内容クリアあり（担当者もログインユーザーに）
  const handleClearConfirmYes = () => {
    applyTodoProcess(true);
    setClearConfirmOpen(false);
  };

  // 確認ダイアログ「いいえ」: 実施日更新と予定/ToDo解除のみ（内容・担当者はそのまま）
  const handleClearConfirmNo = () => {
    applyTodoProcess(false);
    setClearConfirmOpen(false);
  };

  // 確認ダイアログ「キャンセル」/×/ESC/マスク: 何もせず閉じる
  const handleClearConfirmClose = () => {
    setClearConfirmOpen(false);
  };

  // ----------------------------------------------------------------
  // モーダルクローズ後リセット
  // ----------------------------------------------------------------

  const reset = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setAssigneeUserno(null);
    setContent('');
    setIsProvisional(false);
    setIsTodo(false);
    setInternalFileList([]);
    setIsOnsite(false);
    setFollowScheduledTime('');
    setFollowAppointment('');
    setFollowVisitPurpose('');
    setFollowStartTime('');
    setFollowEndTime('');
    setInternalOnsiteCustomers([]);
    setSelectedInternalAttendees([]);
    setFollowOptionFields(null);
    setFollowOptionFieldValues({});
    setFollowOptionFieldErrors({});
    setShowValidationErrors(false);
    setDatePickerOpen(false);
  };

  // ----------------------------------------------------------------
  // JSX
  // ----------------------------------------------------------------

  return (
    <>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: (zIndex ?? 1000) - 1 }} />
      )}
      <Modal
        title={title}
        open={open}
        onCancel={handleCancel}
        width={600}
        centered
        zIndex={zIndex}
        mask={false}
        //destroyOnClose
        afterClose={reset}
        wrapStyle={{
          paddingBottom: `calc(${flutterFooterHeight}px + env(safe-area-inset-bottom, 0px))`,
          boxSizing: 'border-box',
        }}
      styles={{
        content: {
          maxHeight: `calc(100dvh - ${flutterFooterHeight}px - env(safe-area-inset-bottom, 0px) - 40px)`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        body: { flex: 1, overflowY: 'auto', minHeight: 0 },
      }}
      footer={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button style={{ flex: 1, minWidth: 0 }} onClick={handleClear}>クリア</Button>
          <Button
            style={{ flex: 1, marginLeft: 16, minWidth: 0, backgroundColor: '#f0f0f0', borderColor: '#f0f0f0' }}
            onClick={handleCancel}
          >
            キャンセル
          </Button>
          <Button style={{ flex: 1, marginLeft: 8, minWidth: 0 }} type="primary" onClick={handleOk}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <div ref={formTopRef} />

        {/* 訪問/来社 チェックボックス */}
        <Form.Item style={{ marginBottom: 8 }}>
          <Checkbox
            checked={isOnsite}
            onChange={e => setIsOnsite(e.target.checked)}
          >
            訪問/来社
          </Checkbox>
        </Form.Item>

        {/* 予定/ToDo チェックボックス */}
        <Form.Item style={{ marginBottom: 8 }}>
          <Space>
            <Checkbox
              checked={isTodo}
              disabled={isFuture || (isPast && !isTodo)}
              onChange={e => setIsTodo(e.target.checked)}
            >
              予定/ToDo
            </Checkbox>
            {/* 予定/ToDo処理ボタン: 編集モードで予定/ToDo フォローを開いたときのみ表示 */}
            {mode === 'edit' && initialValues?.isTodo && (
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={todoProcessed}
                onClick={handleProcessTodo}
                style={todoProcessed ? undefined : { backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#fff' }}
              >
                予定/ToDo処理
              </Button>
            )}
          </Space>
        </Form.Item>

        {/* フォロー日 */}
        <Form.Item label="フォロー日" required style={{ marginBottom: 24 }}>
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY/MM/DD"
            locale={locale}
            value={date ? dayjs(date) : null}
            onChange={d => setDate(d ? d.format('YYYY-MM-DD') : '')}
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            getPopupContainer={() => document.body}
          />
        </Form.Item>

        {/* 訪問/来社 展開エリア */}
        <AppointmentTimePanel
          isOnsite={isOnsite}
          scheduledTime={followScheduledTime}
          onScheduledTimeChange={setFollowScheduledTime}
          appointment={followAppointment}
          onAppointmentChange={setFollowAppointment}
          visitPurpose={followVisitPurpose}
          onVisitPurposeChange={setFollowVisitPurpose}
          startTime={followStartTime}
          onStartTimeChange={setFollowStartTime}
          endTime={followEndTime}
          onEndTimeChange={setFollowEndTime}
          onsiteCustomers={displayOnsiteCustomers}
          onOnsiteCustomerRemove={id => {
            if (controlledOnsiteCustomers && onOnsiteCustomerRemove) {
              onOnsiteCustomerRemove(id);
            } else {
              setInternalOnsiteCustomers(prev => prev.filter(c => c.customerno !== id));
            }
          }}
          onOnsiteCustomersClear={() => {
            onOnsiteCustomersClear?.();
            if (!controlledOnsiteCustomers) setInternalOnsiteCustomers([]);
          }}
          onCustomerSelectOpen={() => onCustomerSelectOpen?.()}
          mode={mode}
        />

        {/* 担当者 */}
        <Form.Item
          label="担当者"
          required
          validateStatus={showValidationErrors && !assigneeUserno ? 'error' : undefined}
          help={showValidationErrors && !assigneeUserno ? '担当者を選択してください' : undefined}
        >
          <Select
            placeholder="担当者を選択"
            value={assigneeUserno}
            onChange={val => setAssigneeUserno(val)}
            style={{ width: '100%' }}
            loading={isLoadingContactUsers}
            defaultActiveFirstOption={false}
            status={showValidationErrors && !assigneeUserno ? 'error' : undefined}
          >
            {assigneeUserno != null && assigneeUserno < 0 && !contactUsers.some(u => u.userno === assigneeUserno) && (
              <Select.Option key={assigneeUserno} value={assigneeUserno} disabled>
                システム管理者
              </Select.Option>
            )}
            {contactUsers.map(u => (
              <Select.Option key={u.userno} value={u.userno}>
                {u.username} ({u.deptname})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* 同席者（社内）展開エリア */}
        <AppointmentInternalAttendeesSection
          isOnsite={isOnsite}
          selectedInternalAttendees={selectedInternalAttendees}
          onSelectedInternalAttendeesChange={setSelectedInternalAttendees}
          contactUsers={contactUsers}
        />

        {/* 内容 + 仮登録チェック */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span>内容</span>
          <Checkbox checked={isProvisional} onChange={e => setIsProvisional(e.target.checked)}>
            仮登録
          </Checkbox>
        </div>

        <Form.Item style={{ marginBottom: 16 }}>
          <Input.TextArea
            id="follow-content-textarea"
            rows={6}
            placeholder="フォロー内容を入力"
            value={content}
            onChange={e => setContent(e.target.value)}
          />
        </Form.Item>

        {/* アシスト入力・音声入力 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button type="primary" size="middle" icon={<RobotOutlined />} onClick={onAssistClick}>
              アシスト入力
            </Button>
            <Button type="primary" size="middle" icon={<SoundOutlined />} onClick={onVoiceClick}>
              音声入力
            </Button>
          </Space>
        </div>

        {/* 拡張フィールド */}
        {loadingFollowOptionFields ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>拡張フィールドを読み込み中...</div>
          </div>
        ) : (
          followOptionFields?.followoptionfieldlist && followOptionFields.followoptionfieldlist.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <OptionFieldGroup
                title=""
                fields={followOptionFields.followoptionfieldlist.filter(f => f.optionfieldkey !== 'furigana')}
                values={followOptionFieldValues}
                onFieldChange={handleOptionFieldChange}
                prefix="follow"
                errors={followOptionFieldErrors}
                modalZIndex={(zIndex ?? MODAL_Z.L1) + 10}
              />
            </div>
          )
        )}

        {/* コメント（comments 省略時はセクション自体を非表示） */}
        {comments && (
          <Form.Item label="コメント" style={{ marginBottom: 16 }}>
            <CommentSection
              comments={comments}
              currentUserNo={currentUserNo}
              onAdd={onAddComment}
              onEdit={onEditComment}
              onDelete={onDeleteComment}
              showBorder
              addButtonStyle="footer"
              hideLabel
            />
          </Form.Item>
        )}

        {/* 添付ファイル */}
        <Form.Item label="添付ファイル" style={{ marginBottom: 16 }}>
          {displayFileList.length > 0 && (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 8, marginBottom: 16 }}>
              {displayFileList.map((file, index) => (
                <div
                  key={file.uid || file.name}
                  style={{
                    padding: 8,
                    marginBottom: index < displayFileList.length - 1 ? 8 : 0,
                    borderBottom: index < displayFileList.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <PaperClipOutlined style={{ marginRight: 8 }} />
                    <span>{file.name}</span>
                    <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                      {file.size < 1024 * 1024
                        ? `${(file.size / 1024).toFixed(0)} KB`
                        : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', marginTop: 6, gap: 4 }}>
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      style={{ flex: 1 }}
                      onClick={() => onFilePreview?.(file)}
                    />
                    <Button
                      type="text"
                      icon={<DownloadOutlined style={{ color: '#1677ff' }} />}
                      style={{ flex: 1, backgroundColor: '#e6f4ff' }}
                      onClick={() => onFileDownload?.(file)}
                    />
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                      style={{ flex: 1, backgroundColor: '#fff0f0' }}
                      onClick={() => {
                        if (onFileDelete) {
                          onFileDelete(file, index);
                        } else {
                          setInternalFileList(prev => prev.filter((_, i) => i !== index));
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button
            icon={<UploadOutlined />}
            onClick={() => {
              if (!assigneeUserno) {
                setShowValidationErrors(true);
                return;
              }
              onFileAdd?.({
                date,
                assigneeUserno,
                content,
                isProvisional,
                isTodo,
                fileList: displayFileList,
                isOnsite,
                followScheduledTime,
                followAppointment,
                followVisitPurpose,
                followStartTime,
                followEndTime,
                onsiteCustomers: displayOnsiteCustomers,
                selectedInternalAttendees,
                optionFieldValues: followOptionFieldValues,
              });
            }}
          >
            ファイル追加
          </Button>
        </Form.Item>

        {(onNotifSendClick || onNotifHistoryClick || onShowRelatedFollow || onShowLinkFollow) && (
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px 16px', marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(onNotifSendClick || onNotifHistoryClick) && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'send', label: '送信', icon: <SendOutlined />, onClick: () => onNotifSendClick?.(assigneeUserno, isTodo) },
                    { key: 'history', label: '履歴', icon: <HistoryOutlined />, onClick: () => onNotifHistoryClick?.() },
                  ],
                }}
              >
                <Button size="small" icon={<SendOutlined />}>
                  通知 <DownOutlined style={{ fontSize: 10 }} />
                </Button>
              </Dropdown>
            )}

            {/* 関連フォロー表示 */}
            {onShowRelatedFollow && (
              <Button
                size="small"
                icon={<ShareAltOutlined />}
                onClick={onShowRelatedFollow}
              >
                関連フォロー表示
              </Button>
            )}

            {/* フォローの紐づけ */}
            {onShowLinkFollow && (
              <Button
                size="small"
                icon={<LinkOutlined />}
                onClick={onShowLinkFollow}
              >
                フォローの紐づけ
              </Button>
            )}
          </div>
        )}
      </Form>
      </Modal>

      {/* 予定/ToDo処理: フォロー内容クリア確認ダイアログ */}
      <Modal
        open={clearConfirmOpen}
        title="予定/ToDo 処理"
        centered
        zIndex={(zIndex ?? MODAL_Z.L1) + 50}
        getContainer={() => document.body}
        onCancel={handleClearConfirmClose}
        footer={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button style={{ flex: 1, minWidth: 0 }} onClick={handleClearConfirmClose}>キャンセル</Button>
            <Button style={{ flex: 1, marginLeft: 8, minWidth: 0 }} onClick={handleClearConfirmNo}>いいえ</Button>
            <Button
              style={{ flex: 1, marginLeft: 8, minWidth: 0, backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
              type="primary"
              onClick={handleClearConfirmYes}
            >
              はい
            </Button>
          </div>
        }
      >
        <p>フォロー内容をクリアしますか？</p>
      </Modal>
    </>
  );
};

export default FollowFormModal;
